import { useCallback, useEffect, useMemo, useRef } from "react";

import LZString from "lz-string";

import {
    AnalyticsEventPayload,
    UIAnalyticsEvent,
    useAnalyticsEvents
} from "@atlaskit/analytics-next";
import FeatureGates, {
    AnalyticsWebClient,
    type GetExperimentValueOptions
} from "@atlaskit/feature-gate-js-client";

import { GASv3 } from "../../analytics/atl-analytics/gasv3";
import { useAnalyticsClient } from "../../analytics/wac-analytics/analytics-client";
import { Cookie } from "../../utils/cookie";
import { claimExposureOncePerPage } from "../feature-exposed/feature-exposed-once-per-page";
import { addFeatureFlagExposureToSentry } from "../feature-flags/add-feature-flag-exposure-to-sentry";

export const EnrollmentType = {
    ENROLLED: "enrolled",
    NOT_ENROLLED: "notEnrolled",
    INDETERMINATE: "indeterminate"
} as const;

export type Manifest<T> = {
    [k: string]: Experiment<T>;
};

export type MetaData = {
    /**
     * The description.
     */
    description: string;

    /**
     * The expected release data.
     */
    expectedReleaseDate: string;

    /**
     * The expected tuntime.
     */
    expectedRuntime: string;

    /**
     * Additional context for the experiment
     */
    context: ExperimentContext;

    /**
     * Boolean that indicates if experiment is only ran client side
     */
    clientOnly?: boolean;
};

export type ExperimentContext = {
    /**
     * Urls that experiment will run on
     * Accepts singular urls such as "/software/jira", "/software/jira/pricing"
     * or wildcard "*" indicating experiment is global
     * or combination of both such as "/agile/*"
     */
    urls: string[];
};

export type Targeting = {
    /**
     * Function indicating whether the targeting cohort has been met.
     * @returns {boolean | undefined}
     */
    cohort: () => boolean | undefined;
};
export type ExperimentOptions<T> = {
    /**
     * Boolean indicating whether to fire an experiment exposure event.
     */
    fireExperimentExposure: boolean;

    /**
     * Function to check returned value
     * @param value The value
     * @returns {T}
     */
    typeGuard: (value: unknown) => value is T;
};
export type Experiment<T> = {
    /**
     * The experiment name.
     */
    experimentName: string;

    /**
     * The parameter to fetch for the experiment.
     */
    parameterName: string;

    /**
     * The default value to set based on the parameter values.
     */
    defaultValue: T;

    /**
     * The experiment options.
     */
    options: ExperimentOptions<T>;

    /**
     * The experiment options.
     */
    targeting: Targeting;

    /**
     * The metadata.
     */
    metadata: MetaData;
};

export type Indeterminate = {
    /**
     * The experiment enrollment type.
     */
    type: typeof EnrollmentType.INDETERMINATE;
};
export type ExperimentNotEnrolled<T> = {
    /**
     * The experiment enrollment type.
     */
    type: typeof EnrollmentType.NOT_ENROLLED;

    /**
     * The value.
     */
    value: T;
};
export type ExperimentEnrolled<T> = {
    /**
     * The experiment enrollment type.
     */
    type: typeof EnrollmentType.ENROLLED;

    /**
     * The paremeter name.
     */
    parameterName: string;

    /**
     * The value.
     */
    value: T;

    /**
     * Indicates if the experiment falls back to the default value.
     * You can use this value to filter out traffic on track events attributes['usingDefault'].
     */
    usingDefault: boolean;

    /**
     * Function to manually fire an exposure event.
     * @returns {void}
     */
    fireExposure: () => void;
};

export type ExperimentEnrollmentStatus<T> =
    | ExperimentNotEnrolled<T>
    | ExperimentEnrolled<T>
    | Indeterminate;

export type ExperimentWithoutExposureEnrolled<T> = Omit<
    ExperimentEnrolled<T>,
    "fireExposure"
>;

export type ExperimentWithoutExposureEnrollmentStatus<T> =
    | ExperimentNotEnrolled<T>
    | ExperimentWithoutExposureEnrolled<T>
    | Indeterminate;

function getExperimentKeyValue(query: string, key: string): string | null {
    if (!query.startsWith("?")) {
        query = "?" + query;
    }

    const params = new URLSearchParams(query);
    return params.get(key);
}

const X_EXPERIMENTS_KEY = "X-Experiments-Key";

/**
 * Hook that determines experiment enrollment based on experiment manifest.
 * @param experiment The experiment manifest
 * @param metadata The metadata for tracking events
 * @returns {ExperimentEnrollmentStatus}
 */
export function useExperiment<T extends Experiment<unknown>>(
    experiment: T,
    metadata?: object
): ExperimentEnrollmentStatus<T["defaultValue"]> {
    const isInCohort = experiment.targeting.cohort();
    const { experimentName, parameterName, defaultValue, options } = experiment;
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const analyticsClient = useAnalyticsClient();
    const hasFiredRef = useRef(false);

    // Stabilize metadata to prevent unnecessary re-renders
    const stableMetadata = useMemo(() => metadata ?? {}, [metadata]);

    const fireTrackingEvent = useCallback(
        (
            group: unknown,
            info: {
                usingDefault: boolean;
            }
        ) =>
            isInCohort
                ? fireTrackingEvents(
                      experimentName,
                      analyticsClient,
                      createAnalyticsEvent,
                      {
                          ...stableMetadata,
                          ...info
                      },
                      group
                  )
                : () =>
                      console.warn(
                          `${experimentName}: cannot fire tracking event; targeting criteria not met.`
                      ),
        [
            stableMetadata,
            isInCohort,
            experimentName,
            analyticsClient,
            createAnalyticsEvent
        ]
    );

    const experimentEnrollment =
        useMemo((): ExperimentEnrollmentStatus<unknown> => {
            const status = getExperimentStatus(
                experimentName,
                parameterName,
                defaultValue,
                isInCohort,
                options
            );

            if (status.type === EnrollmentType.ENROLLED) {
                return {
                    ...status,
                    fireExposure: () => {
                        if (!options.fireExperimentExposure) {
                            fireTrackingEvent(status.value, {
                                usingDefault: status.usingDefault
                            });

                            try {
                                FeatureGates.manuallyLogExperimentExposure(
                                    experimentName
                                );
                            } catch (error) {
                                console.warn(
                                    `Failed to fire exposure event for ${experimentName}: ${error}`
                                );
                            }
                        } else {
                            console.warn(
                                `Exposure events for ${experimentName} are automatic and cannot be fired manually.`
                            );
                        }
                    }
                };
            }
            return status;
        }, [
            isInCohort,
            experimentName,
            parameterName,
            defaultValue,
            options,
            fireTrackingEvent
        ]);

    useEffect(() => {
        if (
            typeof window !== "undefined" &&
            experimentEnrollment.type === EnrollmentType.ENROLLED
        ) {
            const xExperimentsKey = Cookie.readCookie(X_EXPERIMENTS_KEY);
            if (xExperimentsKey) {
                const decodedExperimentsKey = LZString.decompressFromBase64(
                    xExperimentsKey.split("~")[1] as string
                );
                const evaluatedValueInExpKey = getExperimentKeyValue(
                    decodedExperimentsKey,
                    experimentName
                );
                if (
                    evaluatedValueInExpKey != null &&
                    String(experimentEnrollment.value) !==
                        evaluatedValueInExpKey
                ) {
                    const edgeTraceId = Cookie.readCookie(
                        "X-Experiments-Trace-Id"
                    );

                    const featureGatesAnonIdUsedInSSR =
                        window?.featureGateBootstrapValues.identifiers
                            .analyticsAnonymousId;
                    analyticsClient?.sendOperationalEvent({
                        action: "mismatched",
                        actionSubject: "experimentValue",
                        source: "useExperiment",
                        attributes: {
                            experimentName: experiment.experimentName,
                            route: window.location.pathname,
                            // @ts-ignore
                            anonIdUsedInSsrBeforeOverride: window?.ssrAnonId,
                            anonIdUsedInSSR: featureGatesAnonIdUsedInSSR,
                            anonIdInAnalyticsClient:
                                analyticsClient.getAnonymousId(),
                            experimentKeyValue: evaluatedValueInExpKey,
                            traceId: edgeTraceId,
                            experimentValue: String(experimentEnrollment.value),
                            // @ts-ignore
                            fallback: window.IS_STATIC ? "true" : "false"
                        }
                    });
                }
            }
        }

        // fire tracking if automatic exposures are enabled.
        // Only fire once when user becomes enrolled, not on every state change
        if (
            experimentEnrollment.type === EnrollmentType.ENROLLED &&
            options.fireExperimentExposure &&
            !hasFiredRef.current
        ) {
            const enrolled = experimentEnrollment;
            fireTrackingEvent(enrolled.value, {
                usingDefault: enrolled.usingDefault
            });
            hasFiredRef.current = true;
        }
    }, [
        experimentEnrollment.type,
        options.fireExperimentExposure,
        fireTrackingEvent
    ]);

    return experimentEnrollment;
}

/**
 * Function that determines experiment enrollment based on experiment manifest.
 * Note that this function will not fire exposure events.
 * Often paired with `useExperimentExposure`.
 * @param experiment The experiment manifest
 * @returns {ExperimentWithoutExposureEnrollmentStatus}
 */
export function getExperiment<T extends Experiment<unknown>>(
    experiment: T
): ExperimentWithoutExposureEnrollmentStatus<T["defaultValue"]> {
    const { experimentName, parameterName, defaultValue, options } = experiment;
    const isInCohort = experiment.targeting.cohort();

    return getExperimentStatus(
        experimentName,
        parameterName,
        defaultValue,
        isInCohort,
        // Disable automatic firing of exposure events.
        { ...options, fireExperimentExposure: false }
    );
}

/**
 * Function that determines experiment enrollment based on experiment manifest
 * and fires exposures.
 * @param experiment The experiment manifest
 * @returns {ExperimentWithoutExposureEnrollmentStatus}
 */
export function getExperimentWithExposures<T extends Experiment<unknown>>(
    experiment: T
): ExperimentWithoutExposureEnrollmentStatus<T["defaultValue"]> {
    const { experimentName, parameterName, defaultValue, options } = experiment;
    const isInCohort = experiment.targeting.cohort();

    return getExperimentStatus(
        experimentName,
        parameterName,
        defaultValue,
        isInCohort,
        // Automatically fire experiment exposures
        { ...options, fireExperimentExposure: true }
    );
}

function getExperimentStatus<T>(
    experimentName: string,
    parameterName: string,
    defaultValue: T,
    isInCohort?: boolean,
    options?: GetExperimentValueOptions<T>
): ExperimentWithoutExposureEnrollmentStatus<T> {
    if (isInCohort === undefined) {
        return {
            type: EnrollmentType.INDETERMINATE
        };
    }

    if (!isInCohort) {
        return {
            type: EnrollmentType.NOT_ENROLLED,
            value: defaultValue
        };
    }

    const experimentValue = FeatureGates.getExperimentValue(
        experimentName,
        parameterName,
        undefined,
        options
    );

    const usingDefault = experimentValue === undefined;

    const value = usingDefault ? defaultValue : experimentValue;

    return {
        type: EnrollmentType.ENROLLED,
        parameterName,
        value,
        usingDefault
    };
}

/**
 * Function to fire tracking events.
 * @param isInCohort Boolean indicating whethere the user is in cohort
 * @param analyticsClient The analytics client
 * @param createAnalyticsEvent Function to create analyitics event
 * @param experimentName The experiment name
 * @param metadata The metadata
 * @returns {void}
 */
export function fireTrackingEvents(
    experimentName: string,
    analyticsClient: AnalyticsWebClient | null,
    createAnalyticsEvent: (payload: AnalyticsEventPayload) => UIAnalyticsEvent,
    metadata: object,
    group: unknown
): void {
    if (!analyticsClient) {
        return;
    }

    if (!claimExposureOncePerPage(experimentName, String(group))) {
        return;
    }

    // register feature flag exposure with Sentry client
    addFeatureFlagExposureToSentry(experimentName, group);

    const traceId = Cookie.readCookie("X-Experiments-Trace-Id");
    // fire feature exposed event
    createAnalyticsEvent({
        source: experimentName,
        actionSubject: "feature",
        action: "exposed",
        actionSubjectId: experimentName,
        attributes: GASv3.enrichEventData({
            ...metadata,
            group,
            edgeTraceId: traceId ? traceId : undefined
        }),
        eventType: "track"
    }).fire();
}
